<template>
  <div class="content mb-5">
    <b-row class="mb-3 d-flex align-items-center">
      <b-col cols="12" sm="8">
        <h2 class="mb-0">
          {{ this.$t("Your Protection and Filtering at a Glance") }}
        </h2>
      </b-col>
      <b-col cols="12" sm="4" class="d-flex justify-content-end">
        <span>{{ todaysDate }}</span>
      </b-col>
    </b-row>

    <b-row class="mb-2">
      <b-col cols="12" sm="6" md="6" lg="3" class="mb-3 pr-md-2">
        <t-card-trend
          data-test-id="dashboard"
          :title="this.$t('Requests')"
          :figure="systemStats.overview.requests || 0"
          :loading="loading"
        />
      </b-col>
      <b-col cols="12" sm="6" md="6" lg="3" class="mb-3 px-md-2">
        <t-card-trend
          data-test-id="dashboard"
          :title="this.$t('Unique Domains')"
          :figure="systemStats.overview.unique_domains || 0"
          :loading="loading"
        />
      </b-col>
      <b-col cols="12" sm="6" md="6" lg="3" class="mb-3 px-md-2">
        <t-card-trend
          data-test-id="dashboard"
          :title="this.$t('Allowed')"
          :figure="systemStats.overview.allowed || 0"
          :loading="loading"
        />
      </b-col>
      <b-col cols="12" sm="6" md="6" lg="3" class="mb-3 pl-md-2">
        <t-card-trend
          data-test-id="dashboard"
          :title="this.$t('Blocked')"
          :figure="systemStats.overview.blocked || 0"
          :loading="loading"
        />
      </b-col>
    </b-row>

    <b-row class="mb-3">
      <b-col>
        <t-quick-actions
          horizontal
          :title="this.$t('Global Actions')"
          :actions="quickActions.global"
          :loading="loading"
          @on-action-click="onQuickActionClick"
        />
      </b-col>
    </b-row>
    <section class="all-customers">
      <b-row class="mb-4">
        <b-col sm="12">
          <t-data-table
            data-test-id="msps-data-table"
            with-filter
            :loading="loading"
            :title="$t('All MSPs')"
            :filter-placeholder="$t('Search MSPs')"
            :table-data="dataSet"
            :actions="tableActions"
            :columnsOnLoad="7"
            :disable-select-all="true"
            :single-select="true"
            user-role="system"
            @on-delete="onDelete"
            @on-export="onExport"
            @on-add="onAdd"
            @on-row-clicked="onRowClick"
            :searchExports="searchExports"
            @export-headers="exportedHeaders"
          />
        </b-col>
      </b-row>
    </section>
  </div>
</template>

<route>
{
  "name": "System Overview",
  "meta": {
    "authorize" : [1, 2]
  }
}
</route>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import dayjs from "dayjs";

export default {
  layout: "default",
  data() {
    return {
      chartLegend: "",
      selectedDate: "",
      currentHeaders: null,
      loading: true,
      tableActions: {
        add: {
          enabled: true,
          actions: [
            {
              text: this.$t("Add MSP"),
              value: {
                modal: "t-add-msps",
                title: this.$t("Add an MSP"),
                subtitle: this.$t("New"),
                props: {
                  size: "lg",
                  okTitle: this.$t("Add MSP"),
                },
              },
            },
          ],
        },
        export: { enabled: false },
        import: { enabled: false },
        delete: { enabled: false },
      },
      searchExports: ["name", "email"],
      dataSet: {
        fields: [
          { value: "name", text: this.$t("Name"), sortable: true },
          { value: "email", text: this.$t("Email"), sortable: true },
          {
            value: "description",
            text: this.$t("Description"),
            sortable: true,
          },
          {
            value: "total_licenses_count",
            text: this.$t("License Count"),
            sortable: true,
          },
          { value: "created_at", text: this.$t("Created At"), sortable: true },
          { value: "id", text: this.$t("MSP ID"), sortable: false },
          { value: "policy_id", text: this.$t("Policy ID"), sortable: false },
          { value: "uuid_str", text: this.$t("MSP UUID"), sortable: false },
        ],
        items: [],
      },
      quickActions: {
        global: [
          {
            title: this.$t("Allow Domain"),
            testID: "dashboard-quick-actions-allow-domain",
            value: "allowDomainMSP",
            icon: "check-square",
            iconVariant: "secondary",
          },
          {
            title: this.$t("Block Domain"),
            testID: "dashboard-quick-actions-block-domain",
            value: "blockDomainMSP",
            icon: "x-square",
            iconVariant: "secondary",
          },
          {
            title: this.$t("Edit Default Policy"),
            testID: "dashboard-quick-actions-edit-default-policy",
            value: "editDefaultPolicy",
            icon: "shield",
            iconVariant: "secondary",
          },
        ],
      },
      chartColours: [
        "#058DC7",
        "#50b432",
        "#ed561b",
        "#edef00",
        "#24cbe5",
        "#64e572",
        "#ff9655",
        "#fff263",
        "#6af9c4",
        "#b2deff",
      ],
      chartData: {
        small: {
          highlight: "321",
          difference: -19,
          title: "Line",
          labels: Array.from({ length: 5 }, () =>
            Math.floor(Math.random() * 40)
          ),
          datasets: [
            {
              borderColor: this.$chartColours.allowed,
              backgroundColor: this.$chartColours.allowed,
              label: "Test Dataset 1",
              data: Array.from({ length: 5 }, () =>
                Math.floor(Math.random() * 40)
              ),
              fill: false,
            },
          ],
        },
      },
    };
  },
  computed: {
    ...mapGetters("stats", ["systemStats"]),
    ...mapGetters("users", ["userAccounts"]),
    ...mapGetters("authentication", ["selectedUser"]),
    ...mapGetters("msps", ["msps"]),
    todaysDate() {
      return dayjs(this.selectedDate).format("dddd, D MMMM YYYY ");
    },
    webTraffic() {
      return this.organiseWebTrafficChart(this.systemStats.recentTraffic.data);
    },
    topCategoriesAllowed() {
      return this.systemStats.topCategories.allowed.data.slice(0, 5);
    },
    topCategoriesBlocked() {
      return this.systemStats.topCategories.blocked.data.slice(0, 5);
    },
  },
  methods: {
    ...mapActions("stats", ["fetchSystemOverview"]),
    ...mapActions("pageMeta", ["setPageTitle"]),
    ...mapActions("msps", ["fetchMsps", "deleteMsp"]),
    ...mapMutations("modal", ["createModal", "showModal"]),
    ...mapActions("authentication", ["setUser", "setMspUser", "setMspId"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("userInterface", ["fetchInterface"]),

    generateLegend(legend) {
      this.chartLegend = legend;
    },
    onQuickActionClick(action) {
      switch (action.value) {
        case "allowDomainMSP":
          this.createModal({
            modal: "t-add-domain",
            dataTestId: "modal-add-domain-allow",
            title: this.$t("Allow a domain"),
            subtitle: this.$t("All accounts"),
            modalData: {
              type: "allow",
              toPolicy: false,
              isGlobal: true,
            },
            props: {
              size: "lg",
              okTitle: this.$t("Allow Domain"),
            },
          });
          this.showModal();
          break;

        case "blockDomainMSP":
          this.createModal({
            modal: "t-add-domain",
            dataTestId: "modal-add-domain-block",
            title: this.$t("Block a domain"),
            subtitle: this.$t("All accounts"),
            modalData: {
              type: "block",
              toPolicy: false,
              isGlobal: true,
            },
            props: {
              size: "lg",
              okTitle: this.$t("Block Domain"),
            },
          });
          this.showModal();
          break;

        case "editDefaultPolicy":
          this.$router.push("/system/global_settings/global_default_policy");
          break;

        case "allowDomain":
          this.$router.push("/content_filtering/domains");
          break;

        case "blockLocation":
        case "addLocation":
          this.$router.push("/system/environment/locations");
          break;

        case "customisePolicy":
          this.$router.push("/content_filtering/policies");
          break;

        default:
          break;
      }
    },
    onFilterUpdate(id) {
      this.selectedView = id;
    },
    onTableFilterUpdate(id) {
      this.selectedTableView = id;
    },
    topChartDataset(dataset) {
      return {
        labels: dataset.map((item) => item.name),
        datasets: [
          {
            data: dataset.map((item) => item.count),
            backgroundColor: this.chartColours,
            hoverOffset: 4,
          },
        ],
      };
    },
    organiseWebTrafficChart(data) {
      if (!data) return [];
      data.pop();
      return {
        labels: data.map((item) => dayjs(item.date).format("hh:mm")),
        datasets: [
          {
            label: "Allowed",
            borderColor: this.$chartColours.allowed,
            backgroundColor: this.$chartColours.allowed,
            data: data.map((item) => item.allowed),
            fill: false,
          },
          {
            label: "Blocked",
            borderColor: this.$chartColours.blocked,
            backgroundColor: this.$chartColours.blocked,
            data: data.map((item) => item.blocked),
            fill: false,
          },
        ],
      };
    },
    fectchMSPDashboardData() {
      return Promise.all([
        this.fetchSystemOverview({
          date: this.selectedDate,
        }),
      ]);
    },
    async onDelete(msp) {
      try {
        await this.deleteMsp({ id: msp[0].id });

        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Deleted: X", { item: msp.length }),
          duration: 2000,
          variant: "success",
        });
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
    exportedHeaders(headers) {
      this.currentHeaders = headers;
    },
    onExport(items) {
      if (items.length) {
        const exportData = items.map((res) => {
          let payload = {};
          this.currentHeaders.forEach((headers) => {
            return (payload[headers.value] = res[headers.value]);
          });

          return payload;
        });
        this.$downloadCSV(exportData, "msps");
      } else {
        this.displayToast({
          title: this.$t("Failure"),
          message: this.$t("No msps in table"),
          duration: 2000,
          variant: "danger",
        });
      }
    },
    onAdd(modal) {
      this.createModal(modal);
      this.showModal();
    },
    onRowClick(msp) {
      this.setMspUser(msp);
      this.setMspId(msp.id);
      localStorage.setItem("selectedMsp", msp.id);
      this.$router.push("/msp/overview");
    },
  },
  watch: {
    msps: {
      handler() {
        this.dataSet.items = this.msps;
      },
      immediate: true,
    },
  },
  async created() {
    this.setPageTitle(this.$t("Overview"));
    this.selectedDate = dayjs().format("YYYY-MM-DD");
    try {
      await this.fetchInterface({ mspId: null });
      await this.fectchMSPDashboardData();
      await this.fetchMsps({ id: this.id });
    } finally {
      this.dataSet.items = this.msps;
      this.loading = false;
    }
  },
};
</script>

<style scoped lang="scss">
.chart-color-ref {
  width: 12px;
  height: 12px;
  display: inline-block;
}
</style>
